import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppBar from '../../components/AppBar';
import AppFooter from '../../components/AppFooter';
import ContentGrid from '../../components/ContentGrid';
import GlobalSideMenu from '../../components/GlobalSideMenu';
import { GlobalMicroButton } from '../../components/GlobalMicroButton';
import { useTheme } from '../../context/ThemeProvider';
import styles from './page.module.css';
import { IngestionDocumentationSection } from './IngestionDocContent';
import { RetrievalDocumentationSection } from './RetrievalDocContent';
import { SystemPromptsDocumentationSection } from './SystemPromptsDocContent';

type HealthProbe = {
  key: string;
  label: string;
  url: string;
  status: 'idle' | 'checking' | 'ok' | 'error';
  detail?: string;
};

function StackHealthSection() {
  const toBase = (raw: string): string => raw.replace(/\/+$/, '');
  const [checks, setChecks] = useState<HealthProbe[]>([
    {
      key: 'retrieveKnowledge',
      label: 'Retrieve knowledge function',
      url: toBase(
        process.env.NEXT_PUBLIC_RETRIEVE_KNOWLEDGE_URL ||
          process.env.REACT_APP_RETRIEVE_KNOWLEDGE_URL ||
          'https://europe-west3-thescon-app.cloudfunctions.net/retrieveKnowledge'
      ),
      status: 'idle',
    },
    {
      key: 'ingestDocument',
      label: 'Ingest document function',
      url: toBase(
        process.env.NEXT_PUBLIC_INGEST_DOCUMENT_URL ||
          process.env.REACT_APP_INGEST_DOCUMENT_URL ||
          'https://europe-west3-thescon-app.cloudfunctions.net/ingestDocument'
      ),
      status: 'idle',
    },
    {
      key: 'askChat',
      label: 'Ask chat function',
      url: toBase(
        process.env.NEXT_PUBLIC_ASK_CHAT_URL ||
          process.env.REACT_APP_ASK_CHAT_URL ||
          'https://europe-west3-thescon-app.cloudfunctions.net/askChat'
      ),
      status: 'idle',
    },
  ]);

  const runChecks = async () => {
    setChecks((prev) => prev.map((c) => ({ ...c, status: 'checking', detail: 'Checking...' })));
    await Promise.all(
      checks.map(async (check) => {
        try {
          const resp = await fetch(check.url, { method: 'GET' });
          const ok = resp.status === 200 || resp.status === 400 || resp.status === 405;
          setChecks((prev) =>
            prev.map((c) =>
              c.key === check.key
                ? {
                    ...c,
                    status: ok ? 'ok' : 'error',
                    detail: `HTTP ${resp.status}`,
                  }
                : c
            )
          );
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err);
          setChecks((prev) =>
            prev.map((c) =>
              c.key === check.key
                ? {
                    ...c,
                    status: 'error',
                    detail,
                  }
                : c
            )
          );
        }
      })
    );
  };

  return (
    <section className={styles.section}>
      <h1 className={styles.title}>Live Backend Health</h1>
      <p className={styles.subtitle}>
        Quick ping checks for the currently configured frontend endpoints.
      </p>
      <section className={styles.block}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <strong>Endpoint checks</strong>
          <GlobalMicroButton onClick={runChecks}>Run checks</GlobalMicroButton>
        </div>
        <ul>
          {checks.map((c) => (
            <li key={c.key}>
              <strong>{c.label}:</strong> {c.status.toUpperCase()} {c.detail ? `(${c.detail})` : ''}
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}

export default function DocumentationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const sections = useMemo(
    () => [
      {
        label: 'Infrastructure',
        icon: '/assets/icons/gearshape.svg',
        content: (
          <section className={styles.section}>
            <h1 className={styles.title}>GEX131 / 96 GB VRAM Setup</h1>
            <p className={styles.subtitle}>
              Recommended full concurrent stack for this phase, optimized for speed, depth, and reliability.
            </p>

            <div className={styles.stackGrid}>
              <article className={styles.stackCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitleRow}>
                    <span className={styles.modelIconBox} aria-hidden>
                      <img src="/assets/graphics/llama-icon.svg" alt="" width={22} height={22} />
                    </span>
                    <h2>Llama 3.1 8B Instruct</h2>
                  </div>
                  <span className={styles.badge}>Default Reasoning</span>
                </div>
                <p>
                  Primary general-purpose lane. Llama 3.1 supports 128K context; access requires accepting Meta&apos;s license.
                </p>
              </article>

              <article className={styles.stackCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitleRow}>
                    <span className={styles.modelIconBox} aria-hidden>
                      <img src="/assets/graphics/qwen-icon.svg" alt="" width={22} height={22} />
                    </span>
                    <h2>Qwen 2.5 14B Instruct</h2>
                  </div>
                  <span className={styles.badge}>Deep Reasoning</span>
                </div>
                <p>Heavier lane for difficult prompts and multi-step reasoning. Listed with a 131,072-token context window.</p>
              </article>

              <article className={styles.stackCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitleRow}>
                    <span className={styles.modelIconBox} aria-hidden>
                      <img src="/assets/graphics/microsoft-icon.svg" alt="" width={22} height={22} />
                    </span>
                    <h2>Phi-4 Mini Instruct</h2>
                  </div>
                  <span className={styles.badge}>Fast Parser</span>
                </div>
                <p>Lightweight, fast extractor/parser lane with a 128K context target.</p>
              </article>

              <article className={styles.stackCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitleRow}>
                    <span className={styles.modelIconBox} aria-hidden>
                      <img src="/assets/graphics/mistral-icon.svg" alt="" width={22} height={22} />
                    </span>
                    <h2>Mistral 7B Instruct v0.3 GPTQ 4-bit</h2>
                  </div>
                  <span className={styles.badge}>Critic / Validator</span>
                </div>
                <p>
                  Validation lane. The Red Hat GPTQ build is vLLM-ready; vLLM supports GPTQ/AWQ/Marlin quantization on Ada GPUs.
                </p>
              </article>
            </div>

            <section className={styles.block}>
              <h2>Retrieval Stack</h2>
              <ul>
                <li className={styles.retrievalModelLine}>
                  <span className={styles.modelIconBox} aria-hidden>
                    <img src="/assets/graphics/bge-icon.svg" alt="" width={22} height={22} />
                  </span>
                  <span>
                    <strong>bge-m3</strong> for embeddings
                  </span>
                </li>
                <li className={styles.retrievalModelLine}>
                  <span className={styles.modelIconBox} aria-hidden>
                    <img src="/assets/graphics/bge-icon.svg" alt="" width={22} height={22} />
                  </span>
                  <span>
                    <strong>bge-reranker-v2-m3</strong> for reranking
                  </span>
                </li>
              </ul>
              <p>
                TEI supports both embedding and sequence-classification (rerank-style) model serving.
              </p>
            </section>

            <section className={styles.block}>
              <h2>Reference Links</h2>
              <ul className={styles.links}>
                <li><a href="https://huggingface.co/meta-llama/Llama-3.1-8B-Instruct" target="_blank" rel="noreferrer">Llama 3.1 8B Instruct model card</a></li>
                <li><a href="https://huggingface.co/Qwen/Qwen2.5-14B-Instruct" target="_blank" rel="noreferrer">Qwen 2.5 14B Instruct model card</a></li>
                <li><a href="https://huggingface.co/microsoft/Phi-4-mini-instruct" target="_blank" rel="noreferrer">Phi-4 Mini Instruct model card</a></li>
                <li><a href="https://huggingface.co/RedHatAI/Mistral-7B-Instruct-v0.3-GPTQ-4bit" target="_blank" rel="noreferrer">Mistral 7B Instruct GPTQ 4-bit (Red Hat)</a></li>
                <li><a href="https://docs.vllm.ai/en/latest/features/quantization/" target="_blank" rel="noreferrer">vLLM quantization support</a></li>
                <li><a href="https://github.com/huggingface/text-embeddings-inference" target="_blank" rel="noreferrer">Text Embeddings Inference (TEI)</a></li>
              </ul>
            </section>
          </section>
        ),
      },
      {
        label: 'Ingestion',
        icon: '/assets/icons/document.on.document.svg',
        content: <IngestionDocumentationSection />,
      },
      {
        label: 'Retrieval',
        icon: '/assets/icons/magnifyingglass.svg',
        content: <RetrievalDocumentationSection />,
      },
      {
        label: 'System Prompts',
        icon: '/assets/icons/list.number.svg',
        content: <SystemPromptsDocumentationSection />,
      },
      {
        label: 'Health',
        icon: '/assets/icons/gearshape.svg',
        content: <StackHealthSection />,
      },
      {
        label: 'Knowledge Graph',
        icon: '/assets/icons/list.number.svg',
        content: (
          <section className={styles.section}>
            <h1 className={styles.title}>Entity Graph</h1>
            <p className={styles.subtitle}>
              Placeholder section to keep the structure ready for additional docs.
            </p>
            <div className={styles.block}>
              <ul>
                <li>Architecture diagrams</li>
                <li>Routing and model assignment rules</li>
                <li>Ops runbooks and upgrade checklists</li>
              </ul>
            </div>
          </section>
        )
      },
      {
        label: 'Markdown',
        icon: '/assets/icons/list.number.svg',
        content: (
          <section className={styles.section}>
            <h1 className={styles.title}>Markdown</h1>
            <p className={styles.subtitle}>
              Placeholder section to keep the structure ready for additional docs.
            </p>
            <div className={styles.block}>
              <ul>
                <li>Architecture diagrams</li>
                <li>Routing and model assignment rules</li>
                <li>Ops runbooks and upgrade checklists</li>
              </ul>
            </div>
          </section>
        ),
      },
    ],
    []
  );

  const sectionSlugs = useMemo(
    () => [
      'infrastructure',
      'ingestion',
      'retrieval',
      'system-prompts',
      'health',
      'knowledge-graph',
      'markdown',
    ],
    []
  );

  const [selectedSection, setSelectedSection] = useState(() => {
    const sectionParam = searchParams.get('section');
    if (!sectionParam) return 0;
    const indexFromParam = sectionSlugs.indexOf(sectionParam);
    return indexFromParam >= 0 ? indexFromParam : 0;
  });

  useEffect(() => {
    const sectionParam = searchParams.get('section');
    const indexFromParam = sectionParam ? sectionSlugs.indexOf(sectionParam) : -1;
    const nextIndex = indexFromParam >= 0 ? indexFromParam : 0;
    if (nextIndex !== selectedSection) {
      setSelectedSection(nextIndex);
    }
  }, [searchParams, sectionSlugs, selectedSection]);

  const handleSectionChange = (nextIndex: number) => {
    setSelectedSection(nextIndex);
    const nextSlug = sectionSlugs[nextIndex] || sectionSlugs[0];
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('section', nextSlug);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div>
      <AppBar />
      <ContentGrid mobileRows="64px auto 1fr 48px" rows="64px 1fr 48px">
        <div className={styles.toolbarLeft}>
          <div className={styles.backButton}>
            <GlobalMicroButton
              title="Back"
              icon={
                <img
                  src={isDarkMode ? '/assets/icons/chevron.left.svg' : '/assets/icons/chevron.left.black.svg'}
                  alt=""
                  width={8}
                  height={8}
                />
              }
              onClick={() => navigate('/home')}
            />
          </div>
          <b>Documentation</b>
        </div>

        <div className={styles.toolbarRight} />

        <div className={styles.sideMenuContainer}>
          <GlobalSideMenu
            items={sections.map((section) => ({
              label: section.label,
              content: null,
              icon: section.icon,
            }))}
            initialIndex={selectedSection}
            onChange={handleSectionChange}
          />
        </div>

        <main className={styles.mainContent}>{sections[selectedSection]?.content}</main>

        <AppFooter inline />
      </ContentGrid>
    </div>
  );
}
